/**
 * AgentEditorPanel Component
 * Configuration panel for settings that don't fit in the visual canvas
 * (Knowledge, LLM, Notifications, Products, Advanced)
 */

import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Tab, Nav, Button, Offcanvas, Spinner } from 'react-bootstrap';
import {
  IconBook,
  IconBell,
  IconShoppingCart,
  IconBrain,
  IconSettings,
  IconDeviceFloppy,
  IconX,
  IconUser,
  IconBuilding,
  IconAdjustments,
  IconPuzzle,
  IconSparkles,
  IconPlug,
  IconPlugConnected,
  IconPhoto,
  IconKey,
  IconTargetArrow,
  IconAddressBook,
} from '@tabler/icons-react';

// Tab Components - Only those that don't fit in the canvas
import {
  KnowledgeTab,
  LLMConfigTab,
  NotificationsConfigTab,
  ProductsTab,
  AdvancedConfigTab,
  IdentityConfigTab,
  BusinessInfoTab,
  BehaviorConfigTab,
  CapabilitiesTab,
  HumanizationConfigTab,
  SalesConfigTab,
  IntegrationsTab,
  ChannelsTab,
  MediaGalleryTab,
  ApiKeysTab,
  AgentContactsTab,
} from './tabs';

import { useAgentsFlow } from '../contexts/AgentsFlowContext';
import { useAgentsFlowApi } from '../hooks/useAgentsFlowApi';
import NotificationsContext from '../../../contexts/NotificationsContext';
import { validateLLMConfig } from '../utils/graphValidation';
import './AgentEditorPanel.css';

// ===============================================
// ICON MAP FOR TABS
// ===============================================

const TAB_ICONS = {
  identity: IconUser,
  business: IconBuilding,
  capabilities: IconPuzzle,
  behavior: IconAdjustments,
  sales: IconTargetArrow,
  humanization: IconSparkles,
  knowledge: IconBook,
  llm: IconBrain,
  apikeys: IconKey,
  notifications: IconBell,
  products: IconShoppingCart,
  channels: IconPlugConnected,
  integrations: IconPlug,
  media: IconPhoto,
  contacts: IconAddressBook,
  advanced: IconSettings,
};

// ===============================================
// TAB DEFINITIONS - Only canvas-external configs
// ===============================================

const EDITOR_TABS = [
  { id: 'identity', name: 'Identidade', component: IdentityConfigTab },
  { id: 'business', name: 'Negócio', component: BusinessInfoTab },
  { id: 'knowledge', name: 'Conhecimento', component: KnowledgeTab },
  { id: 'behavior', name: 'Comportamento', component: BehaviorConfigTab },
  { id: 'humanization', name: 'Humanização', component: HumanizationConfigTab },
  { id: 'llm', name: 'Modelo IA', component: LLMConfigTab },
  { id: 'apikeys', name: 'API Keys', component: ApiKeysTab },
  { id: 'products', name: 'Produtos', component: ProductsTab, showIf: 'hasSalesCapability' },
  { id: 'media', name: 'Galeria de Mídias', component: MediaGalleryTab },
  { id: 'contacts', name: 'Contatos', component: AgentContactsTab },
  { id: 'channels', name: 'Canais', component: ChannelsTab },
  { id: 'integrations', name: 'Integrações', component: IntegrationsTab },
  { id: 'notifications', name: 'Notificações', component: NotificationsConfigTab },
  { id: 'sales', name: 'Técnicas de Vendas', component: SalesConfigTab },
  // { id: 'advanced', name: 'Avançado', component: AdvancedConfigTab }, DESATIVADO POR ENQUANTO
];

// ===============================================
// MAIN COMPONENT
// ===============================================

const AgentEditorPanel = ({ show, onHide, agentId }) => {
  const { state, setUnsavedChanges } = useAgentsFlow();
  const { updateAgent, loadAgent } = useAgentsFlowApi();
  const { setNotify } = useContext(NotificationsContext);
  const { editor, ui, currentAgent } = state;
  const { hasUnsavedChanges } = editor;
  const { isSaving } = ui;

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('identity');

  // Load agent data when panel opens
  useEffect(() => {
    if (show && agentId && agentId !== 'novo') {
      setLoading(true);
      loadAgent(agentId).finally(() => setLoading(false));
    }
  }, [show, agentId, loadAgent]);

  // Check if agent has sales capability (for showing Products tab)
  const hasSalesCapability = useCallback(() => {
    const caps = currentAgent?.capabilities || editor.capabilities || [];
    return caps.some(c =>
      (c.code || c) === 'vendas' ||
      (c.code || c) === 'sales' ||
      (c.code || c) === 'ecommerce'
    );
  }, [currentAgent?.capabilities, editor.capabilities]);

  // Get tab status indicators (memoized to avoid recalculating on every render)
  const getTabStatus = useCallback((tabId) => {
    if (tabId === 'llm') {
      const llmValidation = validateLLMConfig(editor.llm_config);
      return llmValidation.status; // 'complete' | 'incomplete' | 'empty'
    }
    return null;
  }, [editor.llm_config]);

  // Filter tabs based on conditions
  const visibleTabs = EDITOR_TABS.filter(tab => {
    if (tab.showIf === 'hasSalesCapability') {
      return hasSalesCapability();
    }
    return true;
  });

  // Handle tab change
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (agentId && agentId !== 'novo') {
      try {
        await updateAgent(agentId, editor);

        // Reset unsaved changes flag
        setUnsavedChanges(false);

        // Show success notification
        setNotify({
          title: 'Configurações salvas!',
          message: 'As configurações do agente foram atualizadas com sucesso.',
          type: 'success',
          visible: true,
        });

      } catch (error) {
        console.error('Error saving agent:', error);

        // Show error notification
        setNotify({
          title: 'Erro ao salvar',
          message: error.response?.data?.message || 'Não foi possível salvar as configurações.',
          type: 'error',
          visible: true,
        });
      }
    }
  }, [agentId, editor, updateAgent, setNotify, setUnsavedChanges]);

  // Render tab content
  const renderTabContent = useCallback((tabConfig) => {
    const TabComponent = tabConfig.component;
    if (!TabComponent) return null;
    return <TabComponent agentId={agentId} />;
  }, [agentId]);

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="agent-editor-panel"
      style={{ width: '75vw' }}
    >
      <Offcanvas.Header className='d-flex justify-content-between'>
        <Offcanvas.Title className="d-flex align-items-center gap-2">
          <IconSettings size={24} />
          <div>
            <span className="fw-bold">Configurações Avançadas</span>
            {hasUnsavedChanges && (
              <span className="ms-2 text-warning small">(alterações não salvas)</span>
            )}
          </div>
        </Offcanvas.Title>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={isSaving || !agentId || agentId === 'novo'}
          >
            {isSaving ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <IconDeviceFloppy size={16} className="me-1" />
            )}
            Salvar
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={onHide}>
            <IconX size={16} />
          </Button>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-0">
        {loading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Tab.Container activeKey={activeTab} onSelect={handleTabChange}>
            <div className="editor-layout">
              {/* Sidebar Navigation */}
              <Nav variant="pills" className="editor-sidebar flex-column">
                {visibleTabs.map((tab) => {
                  const IconComponent = TAB_ICONS[tab.id];
                  const status = getTabStatus(tab.id);
                  return (
                    <Nav.Item key={tab.id}>
                      <Nav.Link eventKey={tab.id} className="editor-nav-link">
                        {IconComponent && <IconComponent size={18} className="me-2" />}
                        {tab.name}
                        {status && (
                          <span className={`tab-status-indicator ${status}`} title={
                            status === 'complete' ? 'Configurado' :
                            status === 'incomplete' ? 'Incompleto' : 'Não configurado'
                          }>
                            {status === 'complete' ? '✓' : status === 'incomplete' ? '!' : '○'}
                          </span>
                        )}
                      </Nav.Link>
                    </Nav.Item>
                  );
                })}
              </Nav>

              {/* Tab Content */}
              <Tab.Content className="editor-content">
                {visibleTabs.map((tab) => (
                  <Tab.Pane key={tab.id} eventKey={tab.id} className="h-100">
                    {renderTabContent(tab)}
                  </Tab.Pane>
                ))}
              </Tab.Content>
            </div>
          </Tab.Container>
        )}
      </Offcanvas.Body>

    </Offcanvas>
  );
};

export default AgentEditorPanel;
